"use client"

import { useState, useMemo } from "react"
import { Plus, Trash2, RotateCcw } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"
import { eurosEn, numeroEn } from "./formato"

interface Ingrediente {
  id: number
  nombre: string
  cantidad: number
  unidad: string
  mermaPct: number
  precioKg: number
}

const T = {
  es: {
    ingredientes: [
      { id: 1, nombre: "Solomillo ternera", cantidad: 200, unidad: "g", mermaPct: 15, precioKg: 28 },
      { id: 2, nombre: "Patatas", cantidad: 150, unidad: "g", mermaPct: 20, precioKg: 1.2 },
      { id: 3, nombre: "Espárragos", cantidad: 80, unidad: "g", mermaPct: 25, precioKg: 8 },
      { id: 4, nombre: "Salsa reducción", cantidad: 50, unidad: "ml", mermaPct: 0, precioKg: 12 },
    ] as Ingrediente[],
    nombrePlatoDefecto: "Solomillo con guarnición",
    titulo: "Escandallo del plato",
    limpiar: "Limpiar",
    nombrePlato: "Nombre del plato",
    phNombrePlato: "Ej: Solomillo con guarnición",
    raciones: "Raciones",
    foodCostObjetivo: "Food cost objetivo (%)",
    ingredientesTitulo: "Ingredientes",
    phIngrediente: "Ingrediente",
    phCantidad: "Cant.",
    cantidad: "Cantidad",
    uds: "uds",
    unidad: "Unidad",
    merma: "% Merma",
    anadir: "Añadir ingrediente",
    resultados: "Resultados",
    costeRacion: "Coste por ración",
    pvpSugerido: "PVP sugerido",
    margenBruto: "Margen bruto",
    desglose: "Desglose de costes",
    sinNombre: "Sin nombre",
    conMerma: (pct: number) => `(${pct}% merma)`,
    total: "Total",
    consejosTitulo: "📋 Escandallo perfecto",
    consejos: [
      "Incluye SIEMPRE las mermas en el cálculo",
      "Actualiza precios mensualmente",
      "Considera costes indirectos (aceite, sal, gas)",
      "Revisa si el food cost objetivo es realista",
    ],
  },
  en: {
    ingredientes: [
      { id: 1, nombre: "Beef tenderloin", cantidad: 200, unidad: "g", mermaPct: 15, precioKg: 28 },
      { id: 2, nombre: "Potatoes", cantidad: 150, unidad: "g", mermaPct: 20, precioKg: 1.2 },
      { id: 3, nombre: "Asparagus", cantidad: 80, unidad: "g", mermaPct: 25, precioKg: 8 },
      { id: 4, nombre: "Red wine reduction", cantidad: 50, unidad: "ml", mermaPct: 0, precioKg: 12 },
    ] as Ingrediente[],
    nombrePlatoDefecto: "Beef tenderloin with sides",
    titulo: "Dish recipe costing",
    limpiar: "Reset",
    nombrePlato: "Dish name",
    phNombrePlato: "e.g. Beef tenderloin with sides",
    raciones: "Portions",
    foodCostObjetivo: "Target food cost (%)",
    ingredientesTitulo: "Ingredients",
    phIngrediente: "Ingredient",
    phCantidad: "Qty",
    cantidad: "Quantity",
    uds: "units",
    unidad: "Unit",
    merma: "% Waste",
    anadir: "Add ingredient",
    resultados: "Results",
    costeRacion: "Cost per portion",
    pvpSugerido: "Suggested menu price",
    margenBruto: "Gross margin",
    desglose: "Cost breakdown",
    sinNombre: "Unnamed",
    conMerma: (pct: number) => `(${pct}% waste)`,
    total: "Total",
    consejosTitulo: "📋 Getting your recipe costing right",
    consejos: [
      "ALWAYS include waste and trim in the calculation",
      "Update ingredient prices every month",
      "Allow for indirect costs (oil, salt, gas)",
      "Check that your target food cost is realistic",
    ],
  },
}

const defaultState = {
  raciones: 1,
  foodCostObjetivo: 30,
}

let nextId = 100

export default function EscandalloCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const defaultIngredientes = t.ingredientes
  const eur = (n: number) => (idioma === "en" ? eurosEn(n) : `${n.toFixed(2)}€`)
  const pct1 = (n: number) => (idioma === "en" ? numeroEn(n, 1) : n.toFixed(1))
  const [nombrePlato, setNombrePlato] = useState(t.nombrePlatoDefecto)
  const [raciones, setRaciones] = useState(defaultState.raciones)
  const [foodCostObjetivo, setFoodCostObjetivo] = useState(defaultState.foodCostObjetivo)
  const [ingredientes, setIngredientes] = useState<Ingrediente[]>(defaultIngredientes)

  const results = useMemo(() => {
    const costesIngredientes = ingredientes.map((ing) => {
      // Ajustar cantidad por merma
      const cantidadReal = ing.cantidad / (1 - ing.mermaPct / 100)
      // Convertir a kg para calcular coste
      const cantidadKg = cantidadReal / 1000
      const coste = cantidadKg * ing.precioKg
      return {
        ...ing,
        cantidadReal,
        coste,
      }
    })

    const costeTotal = costesIngredientes.reduce((sum, ing) => sum + ing.coste, 0)
    const costePorRacion = raciones > 0 ? costeTotal / raciones : 0

    // PVP sugerido según food cost objetivo
    const pvpSugerido = foodCostObjetivo > 0 ? costePorRacion / (foodCostObjetivo / 100) : 0
    const margenBruto = pvpSugerido - costePorRacion
    const margenBrutoPct = pvpSugerido > 0 ? (margenBruto / pvpSugerido) * 100 : 0

    return {
      costesIngredientes,
      costeTotal,
      costePorRacion,
      pvpSugerido,
      margenBruto,
      margenBrutoPct,
    }
  }, [ingredientes, raciones, foodCostObjetivo])

  const addIngrediente = () => {
    setIngredientes([
      ...ingredientes,
      { id: nextId++, nombre: "", cantidad: 0, unidad: "g", mermaPct: 0, precioKg: 0 },
    ])
  }

  const removeIngrediente = (id: number) => {
    if (ingredientes.length > 1) {
      setIngredientes(ingredientes.filter((i) => i.id !== id))
    }
  }

  const updateIngrediente = (id: number, field: keyof Ingrediente, value: string | number) => {
    setIngredientes(
      ingredientes.map((i) => (i.id === id ? { ...i, [field]: value } : i))
    )
  }

  const reset = () => {
    setNombrePlato(t.nombrePlatoDefecto)
    setRaciones(defaultState.raciones)
    setFoodCostObjetivo(defaultState.foodCostObjetivo)
    setIngredientes(defaultIngredientes)
  }

  const getFoodCostColor = (pct: number) => {
    if (pct < 25) return "text-emerald-600"
    if (pct <= 30) return "text-lime-600"
    if (pct <= 35) return "text-amber-600"
    return "text-red-600"
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.titulo}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.nombrePlato}
              </label>
              <input
                type="text"
                value={nombrePlato}
                onChange={(e) => setNombrePlato(e.target.value)}
                placeholder={t.phNombrePlato}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.raciones}
              </label>
              <input
                type="number"
                value={raciones || ""}
                onChange={(e) => setRaciones(parseInt(e.target.value) || 0)}
                placeholder="1"
                min="1"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.foodCostObjetivo}
              </label>
              <input
                type="number"
                value={foodCostObjetivo || ""}
                onChange={(e) => setFoodCostObjetivo(parseFloat(e.target.value) || 0)}
                placeholder="30"
                min="1"
                max="100"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Ingredientes */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-3">
              {t.ingredientesTitulo}
            </label>
            <div className="space-y-3">
              {ingredientes.map((ing) => (
                <div key={ing.id} className="bg-zinc-50 rounded-xl p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={ing.nombre}
                      onChange={(e) => updateIngrediente(ing.id, "nombre", e.target.value)}
                      placeholder={t.phIngrediente}
                      className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                    />
                    <button
                      onClick={() => removeIngrediente(ing.id)}
                      className="p-2 text-zinc-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <input
                        type="number"
                        value={ing.cantidad || ""}
                        onChange={(e) =>
                          updateIngrediente(ing.id, "cantidad", parseFloat(e.target.value) || 0)
                        }
                        placeholder={t.phCantidad}
                        min="0"
                        className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                      />
                      <span className="text-xs text-zinc-400 mt-0.5 block">{t.cantidad}</span>
                    </div>
                    <div>
                      <select
                        value={ing.unidad}
                        onChange={(e) => updateIngrediente(ing.id, "unidad", e.target.value)}
                        className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                      >
                        <option value="g">g</option>
                        <option value="kg">kg</option>
                        <option value="ml">ml</option>
                        <option value="L">L</option>
                        <option value="uds">{t.uds}</option>
                      </select>
                      <span className="text-xs text-zinc-400 mt-0.5 block">{t.unidad}</span>
                    </div>
                    <div>
                      <input
                        type="number"
                        value={ing.mermaPct || ""}
                        onChange={(e) =>
                          updateIngrediente(ing.id, "mermaPct", parseFloat(e.target.value) || 0)
                        }
                        placeholder="0"
                        min="0"
                        max="100"
                        className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                      />
                      <span className="text-xs text-zinc-400 mt-0.5 block">{t.merma}</span>
                    </div>
                    <div>
                      <input
                        type="number"
                        value={ing.precioKg || ""}
                        onChange={(e) =>
                          updateIngrediente(ing.id, "precioKg", parseFloat(e.target.value) || 0)
                        }
                        placeholder="€/kg"
                        min="0"
                        step="0.1"
                        className="w-full px-2 py-1.5 rounded-lg border border-zinc-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lime-500"
                      />
                      <span className="text-xs text-zinc-400 mt-0.5 block">€/kg</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={addIngrediente}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-lime-600 hover:text-lime-700"
            >
              <Plus className="w-4 h-4" />
              {t.anadir}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.resultados}</h2>

          {/* Main KPIs */}
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-6 text-white">
              <p className="text-sm font-medium text-violet-100 mb-1">{t.costeRacion}</p>
              <p className="text-4xl font-bold">{eur(results.costePorRacion)}</p>
              {nombrePlato && (
                <p className="text-sm text-violet-200 mt-1">{nombrePlato}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-lime-500 to-lime-600 rounded-xl p-4 text-white">
                <p className="text-xs text-lime-100 mb-1">{t.pvpSugerido}</p>
                <p className="text-2xl font-bold">{eur(results.pvpSugerido)}</p>
                <p className="text-xs text-lime-200">Food cost {foodCostObjetivo}%</p>
              </div>
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <p className="text-xs text-zinc-500 mb-1">{t.margenBruto}</p>
                <p className={`text-2xl font-bold ${getFoodCostColor(100 - results.margenBrutoPct)}`}>
                  {eur(results.margenBruto)}
                </p>
                <p className="text-xs text-zinc-400">{pct1(results.margenBrutoPct)}%</p>
              </div>
            </div>
          </div>

          {/* Coste breakdown */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <h3 className="text-sm font-semibold text-zinc-900 mb-3">{t.desglose}</h3>
            <div className="space-y-2">
              {results.costesIngredientes.map((ing) => (
                <div key={ing.id} className="flex justify-between text-sm">
                  <span className="text-zinc-600">
                    {ing.nombre || t.sinNombre}
                    {ing.mermaPct > 0 && (
                      <span className="text-zinc-400 text-xs ml-1">{t.conMerma(ing.mermaPct)}</span>
                    )}
                  </span>
                  <span className="font-medium text-zinc-900">{eur(ing.coste)}</span>
                </div>
              ))}
              <div className="border-t border-zinc-100 pt-2 flex justify-between font-semibold">
                <span className="text-zinc-900">{t.total}</span>
                <span className="text-zinc-900">{eur(results.costeTotal)}</span>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.consejosTitulo}</h3>
            <ul className="text-sm text-lime-700 space-y-1">
              {t.consejos.map((c) => (
                <li key={c}>• {c}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
